import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'

import { interpolate as d3_interpolate } from 'd3-interpolate'
import { arc as d3_arc } from 'd3-shape'
import { select as d3_select } from 'd3-selection'
import SizeHelper from '../chart_helpers/SizeHelper'
import { wrapSvgText } from '../utils/textUtils'
import { formatNumber } from '../utils/formatUtils'
import { pieChartLabels } from '../utils/labelUtils'

/* eslint-disable */

export default class PieChartChart extends BaseChart {
  constructor(...args) {
    super(...args)
  }
  static defaultConfig = {
    mode: {
      type: String,
      default: 'pie',
      options: ['pie', 'donut'],
      exposed: false,
    },
    dataSorting: {
      type: [String],
      default: 'none',
      options: ['none', 'asc', 'desc'],
      exposed: true,
    },
    labelValue: {
      type: [String],
      default: 'none',
      options: ['none', 'abs', 'percent'],
      exposed: true,
    },
    /**
     * If set, all values smaller than this threshold will be grouped in a single sector labeled "Others".
     */
    groupOthersThreshold: {
      type: Number,
      default: 0,
      exposed: true,
    },
    /**
     * If set to `true`, sector values will be show as a percentage of the total.
     */
    showPercentage: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    /**
     * Format for the percentages.
     */
    percentageFormat: {
      type: [String],
      default: '[N]0,0.0',
      exposed: true,
    },
    /**
     * If a value is set, it will be displayed in the center of the donut. If set to `true`, the total sum of the sector values will be displayed.
     */
    centerValue: {
      type: [Boolean, Number, String, Object],
      default: true,
      exposed: true,
    },
    /**
     * If a value is set, it will be displayed in the above the central value of the donut.
     */
    centerTopText: {
      type: [String],
      // default: '<div><b>BOLD</b>&<i>ITALIC</i></div>',
      default: '',
      exposed: true,
    },
    /**
     * If a value is set, it will be displayed in the under the central value of the donut.
     */
    centerBottomText: {
      type: [String],
      // default: `<a href="https://www.google.es" target="_blank">link 1</a><span style="color: #f0f; text-decoration: underline; cursor: pointer" onclick="(function(){
      //   alert('Test Alert!');
      // })();">js function</span>`,
      default: '',
      exposed: true,
    },
    /**
     * If set, all sectors will span at lease this angle. Usefull if some values are too small to be seen.
     */
    minSectorAngle: {
      type: Number,
      default: 0,
      exposed: true,
    },
    /**
     * Thickness of the donut
     */
    donutThickness: {
      type: String,
      default: 'medium',
      options: ['thick', 'medium', 'thin'],
      exposed: true,
    },
    /**
     * Show/hide labels for each sector.
     */
    showLabels: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    TooltipBehavior: {
      getHtml: function (d) {
        let formatter = (value, format) => {
          let match = this.matchFormatString(format || '')
          let numberFormat = match && match[2]
          let thouDel = this.config.thousandsDelimiter
          let decDel = this.config.decimalDelimiter
          return formatNumber(value, numberFormat, thouDel, decDel)
        }
        if (d.data.othersData) {
          let datas = d.data.othersData
          let html = datas.map(dd => {
            return `
            <div class="tip-value important">${this.valueWithUnit(dd.value)}</div>
            <div class="tip-id">
              <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
                { id: dd.id }
              )}; margin-right: 3px"></span>
              ${dd.id}
            </div>
            ${
              this.config.showPercentage
                ? '<div class="secondary">' +
                  formatter(dd.percent, this.config.percentageFormat) +
                  '%</div>'
                : ''
            }
            `
          })
          return html.join('<br/>')
        } else {
          return `
          <div class="tip-value important">${this.valueWithUnit(d.data.value)}</div>
          <div class="tip-id">
            <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
              { id: d.id }
            )}; margin-right: 3px"></span>
            ${d.id}
          </div>
          ${
            this.config.showPercentage
              ? '<div class="secondary">' +
                formatter(d.data.percent, this.config.percentageFormat) +
                '%</div>'
              : ''
          }
          `
        }
      },
      getItemOffset: function ($el, d) {
        let parentRect = this.root.node().getBoundingClientRect()
        let center = this._arc.centroid(d)
        // console.log(parentRect,center)
        return {
          x: parentRect.x + parentRect.width / 2 + center[0],
          y: parentRect.y + parentRect.height / 2 + center[1],
        }
      },
    },
  }

  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper]
  formatter(value, format) {
    let match = this.matchFormatString(format || '')
    let numberFormat = match && match[2]
    let thouDel = this.config.thousandsDelimiter
    let decDel = this.config.decimalDelimiter
    return formatNumber(value, numberFormat, thouDel, decDel)
  }
  beforeHelpers() {
    // this.content = this.root.selectAll('.content').data([null])
    // this.content = this.content.merge(this.content.enter().append('g').attr('class','content'))
  }
  getRadiuses() {
    let radius = 0.85 * Math.min(this.size.width / 2, this.size.height / 2)
    if (this.config.showLabels) radius = radius - 30
    let innerRadius
    if (this.config.mode === 'donut') {
      if (this.config.donutThickness === 'thick') innerRadius = 0.5 * radius
      else if (this.config.donutThickness === 'thin') innerRadius = 0.91 * radius
      else innerRadius = 0.7 * radius
    } else {
      innerRadius = 0.01 * radius
    }
    return { radius, innerRadius }
  }
  draw() {
    function findNeighborArc(i, data0, data1, key, backward) {
      var d
      if (!backward) {
        /* eslint-disable no-cond-assign */
        return (d = findPreceding(i, data0, data1, key))
          ? { startAngle: d.endAngle, endAngle: d.endAngle } /* eslint-disable no-cond-assign */
          : // : (d = findFollowing(i, data0, data1, key)) ? {startAngle: d.startAngle, endAngle: d.startAngle}
            // : null;
            { startAngle: 0, endAngle: 0 }
      } else {
        return (d = findFollowing(i, data0, data1, key))
          ? { startAngle: d.startAngle, endAngle: d.startAngle } /* eslint-disable no-cond-assign */
          : // :(d = findPreceding(i, data0, data1, key)) ? {startAngle: d.endAngle, endAngle: d.endAngle} /* eslint-disable no-cond-assign */
            // : null;
            { startAngle: 2 * Math.PI, endAngle: 2 * Math.PI }
      }
    }

    // Find the element in data0 that joins the highest preceding element in data1.
    function findPreceding(i, data0, data1, key) {
      var m = data0.length
      while (--i >= 0) {
        var k = key(data1[i])
        for (var j = 0; j < m; ++j) {
          if (key(data0[j]) === k) return data0[j]
        }
      }
    }

    // Find the element in data0 that joins the lowest following element in data1.
    function findFollowing(i, data0, data1, key) {
      var n = data1.length,
        m = data0.length
      while (++i < n) {
        var k = key(data1[i])
        for (var j = 0; j < m; ++j) {
          if (key(data0[j]) === k) return data0[j]
        }
      }
    }

    // Store the displayed angles in _current.
    // Then, interpolate from _current to the new angles.
    // During the transition, _current is updated in-place by d3_interpolate.
    function arcTween(a) {
      var i = d3_interpolate(this._current, { ...a, innerRadius, radius })
      this._current = i(0)

      return function (t) {
        let arc = d3_arc().innerRadius(i(t).innerRadius).outerRadius(i(t).radius)
        return arc(i(t))
      }
    }

    function key(d) {
      return d && d.data && d.data.id
    }
    let { radius, innerRadius } = this.getRadiuses()
    let arc = d3_arc().innerRadius(innerRadius).outerRadius(radius)
    this._arc = arc
    this.content.attr('transform', `translate(${this.size.width / 2},${this.size.height / 2})`)

    this.itemsEnter.append('path')

    var data0 = this._prevData || this.data
    var data1 = this.data

    this.itemsEnter
      .select('path')
      .attr('fill', d => this.color(d.data))
      .each(function (d, i) {
        if (data0 === data1) this._current = { startAngle: 0, endAngle: 0 }
        else this._current = findNeighborArc(i, data0, data1, key) || d
      })
      .attr('d', function () {
        return arc(this._current)
      })

    this.itemsEnterTrans
      .delay(0)
      .select('path')
      .attr('fill', d => this.color(d.data))
      .attrTween('d', arcTween)

    this.itemsTrans
      .select('path')
      .attr('fill', d => this.color(d.data))
      .attrTween('d', arcTween)

    this.itemsExitTrans
      .style('opacity', 0)
      .select('path')
      .attrTween('d', function (d, i) {
        return arcTween.call(this, findNeighborArc(i, data1, data0, key, true) || d)
      })

    let centerHeight = 20
    let centerMargin = 35
    let TOP_BOTTOM_OFFSET = 6

    let self = this
    let fontSize = 28
    let centerValue = d3_select(this.$root)
      .selectAll('.center-value')
      .data(this.config.mode === 'donut' && this.config.centerValue ? [null] : [])
    let centerValueEnter = centerValue
      .enter()
      .append('text')
      .attr('class', 'center-value')
      .style('opacity', 0)

    centerValue.exit().transition().style('opacity', 0).remove()
    centerValue = centerValue.merge(centerValueEnter)
    centerValue
      .attr('transform', `translate(${this.size.width / 2},${this.size.height / 2})`)
      .attr('fill', 'currentColor')
      .attr('font-size', fontSize)
      .attr('font-family', 'SourceSansPro-embedded')
      .attr('text-anchor', 'middle')
      // .attr('dominant-baseline','central')
      // .attr('alignment-baseline','central')
      .attr('y', '0.36em')

    // this.formatValue(this.data.reduce((sum,d)=>sum+d.value,0))

    // centerValueEnter.text(this.formatValue(centerValueValue)

    let centerValueValue =
      this.config.centerValue === true
        ? this.data.reduce((sum, dd) => sum + dd.data.value, 0)
        : this.config.centerValue || null
    let isPair =
      centerValueValue &&
      (centerValueValue.hasOwnProperty('left') || centerValueValue.hasOwnProperty('right'))

    centerValueEnter.text('')
    let targetValue
    if (!isPair) {
      if (!isNaN(Number(centerValueValue))) {
        centerValue
          .transition()
          .duration(this._transitionDuration)
          .textTween(function () {
            const i = d3_interpolate(this._current || 0, Number(centerValueValue))
            return function (t) {
              this._current = i(t)
              return self.valueWithUnit(this._current)
            }
          })
          .style('opacity', 1)
        targetValue = self.valueWithUnit(centerValueValue)
      } else {
        // if(centerValueValue.indexOf('<')>=0){
        //   let fo = centerValue.selectAll('foreignObject').data([null],(d)=>d)
        //   let foEnter = fo.enter().append('foreignObject')
        //     .attr('class','foreignObject')
        //   foEnter.append('xhtml:div')
        //     .attr('class','html-container')
        //   fo = fo.merge(foEnter)
        //   fo.select('div').html(centerValueValue)
        //   targetValue = null
        // } else {
        centerValue.text(centerValueValue).transition().style('opacity', 1)
        targetValue = centerValueValue
        // }
      }
    } else {
      centerValue
        .html(
          `<tspan font-size="1em" font-weight="bold" x="-${
            centerValueValue.left.length * 0.3
          }em" dx="-6" y="0.36em">${
            centerValueValue.left
          }</tspan><tspan font-size="0.7em" x="0" y="0.36em"> / </tspan><tspan font-size="0.7em" x="${
            centerValueValue.right.length * 0.3
          }em" dx="6" y="0.36em">${centerValueValue.right}</tspan>`
        )
        .transition()
        .style('opacity', 1)
      targetValue = null
    }

    if (centerValue.node() && targetValue) {
      let tmp = centerValue.text()
      centerValue.text(targetValue)
      let l = centerValue.node().getComputedTextLength()
      while (l > 1.8 * innerRadius && fontSize > 10) {
        fontSize = fontSize - 1
        centerValue.attr('font-size', fontSize)
        l = centerValue.node().getComputedTextLength()
      }
      centerValue.text(tmp)
    }

    // OTHER CENTER TEXTS
    let centerSizes = { width: innerRadius * 1.6, height: innerRadius }

    let svgTexts = [] // [{pos: 'top', text: this.config.centerTopText},{pos: 'bottom', text: this.config.centerBottomText}]
    let divTexts = [] //
    if (this.config.centerTopText && this.config.centerTopText.indexOf('<') >= 0)
      divTexts.push({ pos: 'top', text: this.config.centerTopText })
    else svgTexts.push({ pos: 'top', text: this.config.centerTopText })

    if (this.config.centerBottomText && this.config.centerBottomText.indexOf('<') >= 0)
      divTexts.push({ pos: 'bottom', text: this.config.centerBottomText })
    else svgTexts.push({ pos: 'bottom', text: this.config.centerBottomText })

    let centerTextsDivs = d3_select(this.$root)
      .selectAll('.center-text-div')
      .data(this.config.mode === 'donut' ? divTexts : [])
    let centerTextsSvgs = d3_select(this.$root)
      .selectAll('.center-text-svg')
      .data(this.config.mode === 'donut' ? svgTexts : [])

    // IF DIV TEXTS (NOT GOOD for exporting, but good for advanced devs)
    let centerTextsDivsEnter = centerTextsDivs
      .enter()
      .append('g')
      .attr('class', d => 'center-text-div ' + d.pos)
      .style('opacity', 0)

    centerTextsDivsEnter
      .append('foreignObject')
      .attr('class', 'foreignObject')
      .append('xhtml:div')
      .attr('class', 'html-container')
    centerTextsDivs.exit().transition().style('opacity', 0).remove()
    centerTextsDivs = centerTextsDivs.merge(centerTextsDivsEnter)

    centerTextsDivs.attr(
      'transform',
      d =>
        `translate(${this.size.width / 2 - centerSizes.width / 2},${
          this.size.height / 2 + (d.pos === 'top' ? -centerSizes.height : 0)
        })`
    )

    centerTextsDivs
      .select('.foreignObject')
      .attr('width', centerSizes.width)
      .attr('height', centerSizes.height)

    centerTextsDivs
      .select('.foreignObject')
      .select('.html-container')
      // .style('transform',(d,i)=>`translate(-50%,${i===0?'-100%':'0'})`)
      .style('display', 'flex')
      .style('flex-direction', 'column')
      .style('height', '100%')
      .style('justify-content', d => (d.pos === 'top' ? 'flex-end' : 'flex-start'))
      .style('align-items', 'center')
      .style('text-align', 'center')
      .html(d => d.text)

    centerTextsDivs
      .select('.foreignObject')
      .select('.html-container')
      .transition()
      .style('padding-bottom', d => (this.config.centerValue && d.pos === 'top' ? '20px' : '0px'))
      .style('padding-top', d => (this.config.centerValue && d.pos === 'bottom' ? '20px' : '0px'))

    centerTextsDivs.transition().style('opacity', 1)

    // IF SVG TEXTS (GOOD for exporting)
    let centerTextsSvgsEnter = centerTextsSvgs
      .enter()
      .append('g')
      .attr('class', d => 'center-text-svg ' + d.pos)
      .style('opacity', 0)

    centerTextsSvgsEnter
      .append('g')
      .attr('class', 'foreignObject')
      .append('text')
      .attr('class', 'html-container')

    centerTextsSvgs.exit().transition().style('opacity', 0).remove()
    centerTextsSvgs = centerTextsSvgs.merge(centerTextsSvgsEnter)

    fontSize = 16
    centerTextsSvgs.attr(
      'transform',
      d =>
        `translate(${this.size.width / 2},${
          this.size.height / 2 + (d.pos === 'bottom' ? centerMargin : centerHeight - centerMargin)
        })`
    )
    centerTextsSvgs
      .select('.foreignObject')
      .select('.html-container')
      .attr('fill', 'currentColor')
      .attr('font-size', fontSize)
      .attr('font-family', 'SourceSansPro-embedded')
      .attr('text-anchor', 'middle')
      // .attr('dy','0.36em')
      // .attr('dominant-baseline',(d)=>d.pos==='top'?'central':'central')
      .text(d => d.text)
      .call(wrapSvgText, radius)
      .attr('transform', function (d) {
        let height = d.pos === 'top' ? this.getBBox().height : 0
        return `translate(0,${TOP_BOTTOM_OFFSET - height})`
      })
    centerTextsSvgs.transition().style('opacity', 1)

    // LABELS

    // this.itemsEnter.selectAll('.label').data([null]).enter().append('g').attr('class','label')
    let labels = this.items
      .merge(this.itemsEnter)
      .selectAll('.label')
      .data(d => (this.config.showLabels && this.data.length < 15 ? [d] : []))
    let labelsEnter = labels.enter().append('g').attr('class', 'label')
    labels.exit().attr('opacity', 0)
    this.itemsExitTrans.select('.label').attr('opacity', 0).remove()
    let r0 = radius * 0.05,
      dr = radius * 0.2,
      dh = radius * 0.1

    labelsEnter.append('path').attr('stroke', 'currentColor').attr('fill', 'none')

    labelsEnter
      .append('text')
      .attr('fill', 'currentColor')
      .attr('alignment-baseline', 'middle')
      .attr('font-size', '14')
      .attr('font-family', 'SourceSansPro-embedded')

    //const labelsMerge = labels.merge(labelsEnter)

    let outerCentroid = d => {
      let angle = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2
      return [Math.cos(angle) * radius, Math.sin(angle) * radius]
    }
    let labelPath = d => {
      // let center = this._arc.centroid(d)
      let angle = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2
      let center = outerCentroid(d)
      let sign = center[0] > 0 ? 1 : -1
      let start = [center[0] + Math.cos(angle) * r0, center[1] + Math.sin(angle) * r0]
      // let joint = [center[0] + Math.cos(angle) * dr, center[1] + Math.sin(angle) * dr]
      let joint = [(d.y * Math.cos(angle)) / Math.sin(angle), d.y]
      //let end = [joint[0] + sign * dh, joint[1]]
      let end = [d.x - 3 * sign, d.y]
      //un poco cutre hacerlo con sort...
      let jointRange = [start[0], end[0]]
      jointRange.sort((a, b) => a - b)
      joint[0] = Math.min(Math.max(joint[0], jointRange[0]), jointRange[1])

      return `M ${start} L ${joint} L ${[end]}`
    }
    let labelTextAnchor = d => {
      // let center = this._arc.centroid(d)
      let center = outerCentroid(d)
      let sign = center[0] > 0 ? 1 : -1
      return sign < 0 ? 'end' : 'start'
    }
    let labelTransform = function (d) {
      // let center = this._arc.centroid(d)
      // console.log('LABEL TRANSFORM DATA', this.x)
      let center = outerCentroid(d)
      let angle = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2
      let sign = center[0] > 0 ? 1 : -1
      let joint = [(d.y * Math.cos(angle)) / Math.sin(angle), d.y]
      let end = [joint[0] + sign * dh * 1.2, joint[1]]
      return d.x ? `translate(${d.x}, ${d.y})` : `translate(${end})`
    }
    function pathTween(a) {
      var i = d3_interpolate(this._current, a)
      this._current = i(0)
      return function (t) {
        return labelPath(i(t))
      }
    }
    function transTween(a) {
      var i = d3_interpolate(this._current, a)
      this._current = i(0)
      return function (t) {
        return labelTransform.bind(this)(i(t))
      }
    }

    const labelsMerge = labels.merge(labelsEnter)
    const customScale = d => {
      let center = outerCentroid(d)
      let angle = d.startAngle + (d.endAngle - d.startAngle) / 2 - Math.PI / 2
      let sign = center[0] > 0 ? 1 : -1
      // let start = [center[0]+Math.cos(angle)*r0,center[1]+Math.sin(angle)*r0]
      let joint = [center[0] + Math.cos(angle) * dr, center[1] + Math.sin(angle) * dr]
      let end = [joint[0] + sign * dh * 1.2, joint[1]]
      return end
    }

    labelsMerge.select('text').html(d => {
      switch (this.config.labelValue) {
        case 'none':
          return d.id
        case 'abs':
          return `<tspan alignment-baseline="middle">${
            d.id
          }:</tspan> <tspan alignment-baseline="middle" style="opacity: 1; font-size: 14px; font-weight: bold;">${this.valueWithUnit(
            d.data.value
          )}</tspan>`
        case 'percent':
          return `<tspan alignment-baseline="middle">${
            d.id
          }:</tspan> <tspan alignment-baseline="middle" style="opacity: 1; font-size: 14px; font-weight: bold;">${this.formatter(
            d.data.percent,
            this.config.percentageFormat
          )}%</tspan>`
        default:
          return `<tspan alignment-baseline="middle">${d.id}</tspan>`
      }
    })
    let labelsTrans = labelsMerge.transition().duration(1000)
    //labelsTrans.select('text').attrTween('transform', transTween)
    pieChartLabels(labelsMerge, customScale, this.getRadiuses().radius)

    labelsEnter.attr('opacity', 0)
    labelsEnter.select('path').attr('d', labelPath)
    labelsEnter.select('text').attr('text-anchor', labelTextAnchor)

    labelsMerge.select('text').html(d => {
      switch (this.config.labelValue) {
        case 'none':
          return `<tspan alignment-baseline="middle">${d.id}</tspan>`
        case 'abs':
          return `<tspan alignment-baseline="middle">${
            d.id
          }:</tspan> <tspan alignment-baseline="middle" style="opacity: 1; font-size: 14px; font-weight: bold;">${this.valueWithUnit(
            d.data.value
          )}</tspan>`
        case 'percent':
          return `<tspan alignment-baseline="middle">${
            d.id
          }:</tspan> <tspan alignment-baseline="middle" style="opacity: 1; font-size: 14px; font-weight: bold;">${this.formatter(
            d.data.percent,
            this.config.percentageFormat
          )}%</tspan>`
        default:
          return `<tspan alignment-baseline="middle">${d.id}</tspan>`
      }
    })
    // labelsMerge.select('path') //.attr('opacity', 0)
    labelsTrans = labelsMerge.transition().duration(1000)
    labelsTrans.select('text').attrTween('transform', transTween)
    labelsTrans.attr('opacity', 1)
    labelsTrans
      .select('path')
      // .attr('opacity', 0)
      .attrTween('d', pathTween)
    // .transition()
    // .duration(500)
    // .attr('opacity', 1)
    // .attr('d', labelPath)

    labelsTrans.select('text').attr('text-anchor', labelTextAnchor)
    // .attr('transform', labelTransform)

    // we must reset selection
    this.selection = this.data.filter(d => this.selection.find(dd => dd.id === d.id) !== undefined)
    this._updateSelection()
  }
  setSelectedFunc(sel, d) {
    if (this.config.mode !== 'donut') return
    let { radius, innerRadius } = this.getRadiuses()

    // let offset = (radius - innerRadius)*0.75
    let offset
    if (this.config.donutThickness === 'thin') offset = (radius - innerRadius) * 0.75
    else if (this.config.donutThickness === 'medium') offset = (radius - innerRadius) * 0.15
    else offset = (radius - innerRadius) * 0.1

    radius = radius + offset
    innerRadius = innerRadius - offset
    let arc = d3_arc().innerRadius(innerRadius).outerRadius(radius)
    // let glow = sel.selectAll('.glow').data(this.config.donutThickness==='thin'?[d]:[])
    let glow = sel.selectAll('.glow').data([d])
    glow.exit().remove()
    glow = glow.merge(glow.enter().append('path').attr('class', 'glow'))
    glow
      .attr('d', arc)
      .attr('fill', this.color(d))
      .attr('opacity', 0)
      .transition()
      .duration(200)
      .attr('opacity', 0.25)
  }
  unsetSelectedFunc(sel) {
    if (this.config.mode !== 'donut') return
    let glow = sel.selectAll('.glow').data([])
    glow.exit().remove()
  }
}
